import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, ArrowRightLeft, CheckCircle2, XCircle, Clock, PlayCircle, RefreshCw, Download, FileText } from "lucide-react";
import { api } from "../../lib/api";

const STATUSES = ["all", "requested", "in_progress", "awaiting_cosign", "completed", "returned", "cancelled", "failed"];

const STATUS_STYLES = {
  requested:        "bg-[#c9a961]/12 text-[#a3823f]",
  in_progress:      "bg-[#c9a961]/20 text-[#c9a961]",
  awaiting_cosign:  "bg-[#8a4b7a]/12 text-[#8a4b7a]",
  completed:        "bg-[#6b7c3a]/15 text-[#4d5a24]",
  returned:         "bg-[#b23a48]/10 text-[#b23a48]",
  cancelled:        "bg-black/6 text-black/40",
  failed:           "bg-[#b23a48]/12 text-[#b23a48]",
};

function usd(cents) {
  return `$${(Number(cents || 0) / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}
function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function AdminTransfers() {
  const [filter, setFilter] = useState("requested");
  const [rows, setRows] = useState([]);
  const [threshold, setThreshold] = useState(1000000); // $10k default until backend loads
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [notes, setNotes] = useState({});
  const [stripeRefs, setStripeRefs] = useState({});
  // Whether the current admin can act on transfers (founder-only). Members
  // can VIEW their clients' transfers but cannot approve, complete, or
  // otherwise change status. Backend re-enforces this; UI hides the buttons.
  const [canApprove, setCanApprove] = useState(true);
  useEffect(() => {
    api.get("/admin-auth/me")
      .then(({ data }) => setCanApprove(data?.role === "founder"))
      .catch(() => setCanApprove(true)); // legacy shared-secret caller keeps full access
  }, []);
  // Which of the two founders is signed in / actioning transfers right now.
  // Persisted locally so it doesn't reset between visits.
  const [approver, setApprover] = useState(() => {
    try { return localStorage.getItem("elevare_transfer_approver") || "Kalib"; } catch { return "Kalib"; }
  });
  const chooseApprover = (name) => {
    setApprover(name);
    try { localStorage.setItem("elevare_transfer_approver", name); } catch {}
  };

  const load = async () => {
    setLoading(true);
    try {
      const q = filter && filter !== "all" ? `?status=${filter}` : "";
      const { data } = await api.get(`/admin/vault/transfers${q}`);
      setRows(data?.transfers || []);
      if (data?.dual_sign_threshold_cents) setThreshold(data.dual_sign_threshold_cents);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load transfers.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const patch = async (id, status, note, stripeRef) => {
    setSavingId(id);
    try {
      const payload = { status, note, approved_by: approver };
      if (stripeRef) payload.stripe_ref = stripeRef;
      const { data } = await api.patch(`/admin/vault/transfers/${id}`, payload);
      setRows((prev) => prev.map((r) => r.id === id ? data : r));
      toast.success(`Marked ${status.replace("_", " ")} as ${approver}.`);
      if (filter !== "all" && data.status !== filter) {
        // The row no longer matches the current filter — drop it from view.
        setRows((prev) => prev.filter((r) => r.id !== id));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update transfer.");
    } finally {
      setSavingId(null);
    }
  };

  const counts = useMemo(() => {
    const m = { all: rows.length };
    for (const r of rows) m[r.status] = (m[r.status] || 0) + 1;
    return m;
  }, [rows]);

  // Trigger the admin's browser to download a file we fetch via the api client
  // (so the X-Admin-Secret header is attached). Works for both CSV + PDF.
  const downloadBlob = async (url, fallbackName) => {
    try {
      const { data, headers } = await api.get(url, { responseType: "blob" });
      // Pull the filename from Content-Disposition if the server sent one.
      const cd = headers?.["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/.exec(cd);
      const filename = match ? match[1] : fallbackName;
      const blobUrl = window.URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed.");
      return false;
    }
  };

  const exportCsv = async () => {
    const q = filter && filter !== "all" ? `?status=${filter}` : "";
    const ok = await downloadBlob(`/admin/vault/transfers/export.csv${q}`, "elevare-transfers.csv");
    if (ok) toast.success("Transfers CSV downloaded.");
  };

  const downloadReceipt = async (id) => {
    const ok = await downloadBlob(`/admin/vault/transfers/${id}/receipt.pdf`, `elevare-transfer-${id}.pdf`);
    if (ok) toast.success("Receipt downloaded — also filed to the client's progress.");
  };

  return (
    <div data-testid="admin-transfers">
      {!canApprove && (
        <div data-testid="transfers-locked-banner" className="mb-4 rounded-xl border border-[#c9a961]/40 bg-[#c9a961]/12 px-4 py-3 flex items-start gap-2.5">
          <XCircle className="h-4 w-4 text-[#a3823f] mt-0.5 shrink-0" />
          <div className="text-[12px] leading-relaxed text-[#1e3d3a]">
            <p className="font-medium text-[#a3823f] uppercase tracking-[0.18em] text-[10px]">View-only</p>
            <p className="mt-0.5">Only Kalib and John can approve or update bank transfers. You can review the pipeline here, but the action buttons are locked until a founder signs off.</p>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f]">Bank transfers</p>
          <h2 className="mt-1 font-head text-2xl text-[#1e3d3a]">Pending client requests</h2>
        </div>
        <div className="flex items-center gap-3">
          {canApprove && (
          <div className="inline-flex items-center gap-1 rounded-full bg-white border border-[#1e3d3a]/12 p-1" data-testid="admin-transfers-approver">
            <span className="text-[9px] uppercase tracking-[0.22em] text-black/40 px-2">Approving as</span>
            {["Kalib", "John"].map((name) => (
              <button
                key={name}
                type="button"
                data-testid={`admin-transfers-approver-${name.toLowerCase()}`}
                onClick={() => chooseApprover(name)}
                className={`text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-full transition-colors duration-300 ${
                  approver === name ? "bg-[#1e3d3a] text-white" : "text-[#1e3d3a]/55 hover:text-[#1e3d3a]"
                }`}
              >
                {name}
              </button>
            ))}
          </div>
          )}
          <button
            type="button"
            onClick={exportCsv}
            disabled={loading || rows.length === 0}
            data-testid="admin-transfers-export-csv"
            className="inline-flex items-center gap-2 rounded-full border border-[#1e3d3a]/15 bg-white px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] text-[#1e3d3a] hover:border-[#c9a961] hover:text-[#a3823f] disabled:opacity-60 transition-colors duration-300"
            title="Download the current transfers list as CSV"
          >
            <Download className="h-3.5 w-3.5" />
            Export CSV
          </button>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            data-testid="admin-transfers-refresh"
            className="inline-flex items-center gap-2 rounded-full border border-[#1e3d3a]/15 bg-white px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] text-[#1e3d3a] hover:border-[#c9a961] hover:text-[#a3823f] disabled:opacity-60 transition-colors duration-300"
          >
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Refresh
          </button>
        </div>
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {STATUSES.map((s) => (
          <button
            key={s}
            type="button"
            data-testid={`admin-transfers-filter-${s}`}
            onClick={() => setFilter(s)}
            className={`text-[10px] uppercase tracking-[0.22em] px-3 py-1.5 rounded-full transition-colors duration-300 ${
              filter === s
                ? "bg-[#1e3d3a] text-white"
                : "bg-white text-[#1e3d3a]/55 hover:text-[#1e3d3a] border border-[#1e3d3a]/10"
            }`}
          >
            {s.replace("_", " ")}
            {counts[s] != null && <span className="ml-1.5 opacity-70 tabular-nums">{counts[s]}</span>}
          </button>
        ))}
      </div>

      {/* List */}
      {loading && rows.length === 0 ? (
        <div className="flex items-center gap-2 text-sm text-[#1e3d3a]/55 py-8 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading transfers…
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#1e3d3a]/15 bg-white/60 p-10 text-center">
          <ArrowRightLeft className="h-6 w-6 text-[#a3823f]/50 mx-auto" strokeWidth={1.4} />
          <p className="mt-2 text-sm text-[#1e3d3a]/55">No {filter === "all" ? "" : filter.replace("_", " ")} transfers.</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => {
            const style = STATUS_STYLES[r.status] || "bg-black/5 text-black/50";
            const isOpen = expanded === r.id;
            const canAction = r.status === "requested" || r.status === "in_progress" || r.status === "awaiting_cosign";
            const needsDualSign = (r.amount_cents || 0) >= threshold;
            const signatures = r.signatures || [];
            const alreadySignedByMe = signatures.some((s) => s.name === approver);
            return (
              <li
                key={r.id}
                data-testid={`admin-transfer-${r.id}`}
                className="rounded-xl border border-[#1e3d3a]/10 bg-white overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => setExpanded(isOpen ? null : r.id)}
                  className="w-full flex items-center gap-4 px-4 py-3 text-left hover:bg-[#c9a961]/6 transition-colors duration-200"
                >
                  <span className="font-head text-lg text-[#1e3d3a] tabular-nums shrink-0 min-w-[110px]">{usd(r.amount_cents)}</span>
                  <div className="min-w-0 flex-1 text-xs text-[#1e3d3a]/70 flex items-center gap-2">
                    <span className="truncate">{r.from?.brand} ••••{r.from?.last4}</span>
                    <ArrowRightLeft className="h-3 w-3 text-[#a3823f] shrink-0" />
                    <span className="truncate">{r.to?.brand} ••••{r.to?.last4}</span>
                  </div>
                  <div className="min-w-0 hidden sm:flex flex-col items-end shrink-0 gap-0.5">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/45">{r.client_id}</span>
                    <span className="text-[10px] text-[#1e3d3a]/40">{fmtDate(r.created_at)} · when: {r.when}</span>
                  </div>
                  <span className={`shrink-0 text-[10px] uppercase tracking-[0.22em] px-2.5 py-1 rounded-full ${style}`}>
                    {r.status.replace("_", " ")}
                  </span>
                  {needsDualSign && r.status !== "completed" && r.status !== "cancelled" && r.status !== "failed" && (
                    <span className="shrink-0 text-[9px] uppercase tracking-[0.22em] px-2 py-1 rounded-full bg-[#8a4b7a]/10 text-[#8a4b7a] border border-[#8a4b7a]/20" title="Both Kalib and John must sign transfers over $10,000">
                      2-sig · {signatures.length}/2
                    </span>
                  )}
                </button>
                {isOpen && (
                  <div className="border-t border-[#1e3d3a]/8 bg-[#eae7de]/30 px-4 py-3">
                    {needsDualSign && r.status !== "completed" && (
                      <div className="mb-3 rounded-lg border border-[#8a4b7a]/25 bg-[#8a4b7a]/6 px-3 py-2 text-[11px] text-[#1e3d3a] leading-relaxed">
                        <p className="text-[10px] uppercase tracking-[0.22em] text-[#8a4b7a] mb-1">Two-signature transfer</p>
                        {signatures.length === 0 && (
                          <>Over $10,000 &mdash; both Kalib and John must click "Mark completed" before the money moves.</>
                        )}
                        {signatures.length === 1 && (
                          <>Signed by <strong>{signatures[0].name}</strong> {fmtDate(signatures[0].at)}. Waiting on <strong>{signatures[0].name === "Kalib" ? "John" : "Kalib"}</strong> to co-sign.</>
                        )}
                        {signatures.length >= 2 && (
                          <>Both founders have signed. Completing now…</>
                        )}
                      </div>
                    )}
                    {r.note && (
                      <p className="text-xs text-[#1e3d3a]/70 mb-3">
                        <span className="text-[9px] uppercase tracking-[0.22em] text-[#a3823f] block mb-1">Client note</span>
                        {r.note}
                      </p>
                    )}
                    {r.admin_note && (
                      <p className="text-xs text-[#1e3d3a]/70 mb-3">
                        <span className="text-[9px] uppercase tracking-[0.22em] text-[#6b7c3a] block mb-1">Owner note</span>
                        {r.admin_note}
                      </p>
                    )}
                    {r.completed_at && (
                      <p className="text-[10px] uppercase tracking-[0.22em] text-[#4d5a24] mb-3">
                        Completed {fmtDate(r.completed_at)}{r.approved_by ? ` · by ${r.approved_by}` : ""}
                      </p>
                    )}
                    {/* Download receipt is available on every row so admins can
                        grab a paper trail for any status. Completed & returned
                        additionally auto-file the receipt into the client's
                        Progress tracker (idempotent). */}
                    <div className="mb-3 flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        data-testid={`admin-transfer-receipt-${r.id}`}
                        onClick={() => downloadReceipt(r.id)}
                        className="inline-flex items-center gap-1.5 rounded-full border border-[#1e3d3a]/15 bg-white text-[#1e3d3a] px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] hover:border-[#c9a961] hover:text-[#a3823f] transition-colors duration-300"
                        title="Download this transfer's PDF receipt. For completed / returned transfers this also files it into the client's progress tracker."
                      >
                        <FileText className="h-3.5 w-3.5" /> Download receipt
                      </button>
                      {r.receipt_attached_at && (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] text-[#4d5a24]"
                          title={`Auto-filed to Bank transfers milestone at ${fmtDate(r.receipt_attached_at)}`}
                          data-testid={`admin-transfer-receipt-filed-${r.id}`}
                        >
                          <CheckCircle2 className="h-3 w-3" /> Filed to client
                        </span>
                      )}
                    </div>
                    {canAction && canApprove && (
                      <>
                        <textarea
                          data-testid={`admin-transfer-note-${r.id}`}
                          rows={2}
                          placeholder="Optional note (e.g. ACH ref #123456)"
                          value={notes[r.id] || ""}
                          onChange={(e) => setNotes({ ...notes, [r.id]: e.target.value })}
                          className="w-full text-xs rounded border border-[#1e3d3a]/15 bg-white px-2 py-1.5 focus:border-[#c9a961] focus:outline-none resize-y mb-2"
                        />
                        <input
                          data-testid={`admin-transfer-stripe-ref-${r.id}`}
                          type="text"
                          placeholder="Stripe payout / Treasury ID (po_… / opa_…) — optional, auto-tracks status"
                          value={stripeRefs[r.id] ?? (r.stripe_ref || "")}
                          onChange={(e) => setStripeRefs({ ...stripeRefs, [r.id]: e.target.value })}
                          className="w-full text-[11px] font-mono rounded border border-[#1e3d3a]/15 bg-white px-2 py-1.5 focus:border-[#c9a961] focus:outline-none mb-2"
                        />
                        <div className="flex flex-wrap items-center gap-2">
                          {r.status === "requested" && (
                            <button
                              type="button"
                              data-testid={`admin-transfer-inprogress-${r.id}`}
                              onClick={() => patch(r.id, "in_progress", notes[r.id], stripeRefs[r.id])}
                              disabled={savingId === r.id}
                              className="inline-flex items-center gap-1.5 rounded-full bg-[#c9a961] text-[#1e3d3a] px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] hover:bg-[#1e3d3a] hover:text-white disabled:opacity-60 transition-colors duration-300"
                            >
                              <PlayCircle className="h-3.5 w-3.5" /> Mark in progress
                            </button>
                          )}
                          <button
                            type="button"
                            data-testid={`admin-transfer-complete-${r.id}`}
                            onClick={() => patch(r.id, "completed", notes[r.id], stripeRefs[r.id])}
                            disabled={savingId === r.id || (needsDualSign && alreadySignedByMe)}
                            title={needsDualSign && alreadySignedByMe ? `${approver} has already signed — switch approver above` : ""}
                            className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] text-white px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-60 transition-colors duration-300"
                          >
                            {savingId === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                            {needsDualSign
                              ? (signatures.length === 0 ? "Sign 1 of 2" : (alreadySignedByMe ? `Signed by ${approver}` : "Co-sign & complete"))
                              : "Mark completed"}
                          </button>
                          <button
                            type="button"
                            data-testid={`admin-transfer-fail-${r.id}`}
                            onClick={() => patch(r.id, "failed", notes[r.id], stripeRefs[r.id])}
                            disabled={savingId === r.id}
                            className="inline-flex items-center gap-1.5 rounded-full border border-[#b23a48]/40 text-[#b23a48] px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] hover:bg-[#b23a48]/10 disabled:opacity-60 transition-colors duration-300"
                          >
                            <XCircle className="h-3.5 w-3.5" /> Mark failed
                          </button>
                          <button
                            type="button"
                            data-testid={`admin-transfer-returned-${r.id}`}
                            onClick={() => patch(r.id, "returned", notes[r.id], stripeRefs[r.id])}
                            disabled={savingId === r.id}
                            className="inline-flex items-center gap-1.5 rounded-full border border-[#b23a48]/40 text-[#b23a48] px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] hover:bg-[#b23a48]/10 disabled:opacity-60 transition-colors duration-300"
                            title="ACH was returned by the bank (NSF, closed account, etc.)"
                          >
                            <XCircle className="h-3.5 w-3.5" /> ACH returned
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
